/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * undoManager.js -- undo/redo manager for plots; allows plot to have unlimited undo/redo steps.
 */

/**
 * Construct an "Undo Step" object that holds data and a reference to an object
 * that can undo and redo that step.  Used by Dyn.undoManager
 * @param undoableData {*} - data used to undo/redo
 * @param undoHelper {*} - persistent object that has undo()/redo() interface.
 * @constructor
 */
export class UndoStep {

    constructor( undoableData, undoHelper ) {
        this.undoableData = undoableData;
        this.undoHelper = undoHelper;
        this.extraSteps = null; // stack of dependent undo actions, if any
        this.Dyn = undoHelper.Dyn;
        let Dyn = this.Dyn;
        Dyn.assert( undoableData.actionID, "undoableData must have .actionID string" );
        Dyn.assert( typeof undoableData.actionString === "string", "undoableData must have .actionString string" );
        Dyn.assert( typeof undoableData === "object", "undoableData must be an object" );
        Dyn.assert( undoHelper && typeof undoHelper.undo === "function", "undoHelper is missing .undo()" );
        Dyn.assert( undoHelper && typeof undoHelper.redo === "function", "undoHelper is missing .redo()" );
    }

    addExtraStep( undoStep ) {
        if( ! this.extraSteps ) {
            this.extraSteps = [];
        }
        this.extraSteps.push( undoStep );
    }

    undo() {
        let Dyn = this.Dyn;
        Dyn.assert( typeof this.undoableData === "object" );
        Dyn.assert( this.undoHelper && typeof this.undoHelper.undo === "function" );
        this.undoHelper.undo( this.undoableData );
        if( this.extraSteps ) {
            this.extraSteps.forEach( ( step ) => {
                step.undo();
            } );
        }
    }

    redo() {
        let Dyn = this.Dyn;
        Dyn.assert( typeof this.undoableData === "object" );
        Dyn.assert( this.undoHelper && typeof this.undoHelper.undo === "function" );
        this.undoHelper.redo( this.undoableData );
        if( this.extraSteps ) {
            this.extraSteps.forEach( ( step ) => {
                step.redo();
            } );
        }
    }

    getOrCreatePlotState( wantNewState, createIfMissing = false ) {
        let plotView = this.Dyn.plotView,
            state = wantNewState ? this.undoableData.restoreData : this.undoableData.saveData;
        if( this.undoHelper===plotView && plotView.isSavedPlotState( state )) {
            return state; // return the already captured plot state
        } else if (createIfMissing){
            console.log("getOrCreatePlotState: capture current plot state");
            // else capture current plot state
            if( this.undoHelper.elemPlayContainer ) {
                // Play view
                return this.undoHelper.elemPlayContainer.savePlotState();
            } else {
                // Plot view
                return plotView.save();
            }
        }
    }
} // class UndoStep



/**
 * API for UndoHelper base class.
 *
 * Dyn.undoMangager expect each undoable step to pass in an object that responds to these
 * undo() and redo() calls.  The initial "do" is not handled by the UndoHelper or undoManager.
 *
 * @constructor
 */
export class UndoHelper {

    constructor( Dyn ) {
        this.Dyn = Dyn;
        Dyn.undoManager.init();
    }

    /**
     * Undo an action. Note that undo() should not itself call Dyn.undoManager.saveUndoStep(),
     * but instead use the undoableData to restore the previous state.
     * @param undoableData
     */
    undo( undoableData ) {
        console.log("Dyn.UndoStep.undo() base function needs override");
    }
    /**
     * Redo an action. Note that redo() can simply call the original "do" method (or not)
     * but must also include a call to Dyn.undoManager.saveUndoStep() just like the original "do" method,
     * to make the redo also undoable.
     * @param undoableData
     */
    redo( undoableData ) {
        console.log("Dyn.UndoStep.redo() base function needs override");
    }
} // class UndoHelper

/**
 * Global undo manager, for undo/redo of dataset/plot actions.
 * Handles an unlimited number undo/redo steps.
 * Each undoable step must have both undo data (JSON, typically),
 * and a persistent undo object which response to the undo()/redo() requests
 * that the undoManager sends to it.
 */
export class UndoManager {

    constructor( Dyn ) {
        this.Dyn = Dyn;
        this.undoStack = []; // private, callers should not modify directly
        this.redoStack = []; // private, callers should not modify directly
        this.init();
    }

    init()  {
        if( ! this.undoStack ) { // only initialize once
            this.undoStack = [];
            this.redoStack = [];
        }
    }

    /**
     * Add an undo step to our stack of undo actions.
     * The caller is responsible for the "do" of the action.
     * @param undoableData
     * @param undoHelper
     * @param isUndoOfRedo - true if undo is of a redo
     */
    saveUndoStep( undoableData, undoHelper, isUndoOfRedo ) {
        let Dyn = this.Dyn;
        if( ! isUndoOfRedo ) {
            // if a new "do" action is inserted (but not a redo), we must clear the redo stack,
            // because redo may depend on previous state.
            this.clearRedoStack();
        }
        let undoStep = new Dyn.UndoStep( undoableData, undoHelper )
        this.undoStack.push( undoStep );
        Dyn.debugUndo && console.log( "undoManager.saveUndoStep() #"+this.undoStack.length+" created for "+undoableData.actionString);
        // on user action, do callback
        if ( typeof Dyn.onUserAction !== "undefined" ) {
            Dyn.onUserAction(
                { type: undoStep.undoableData.actionID, value: "Do "+undoStep.undoableData.actionString },
                undoStep.getOrCreatePlotState( true, true ),
                undoStep.getOrCreatePlotState( false ));
        }
        //console.log( "Dyn.undoManager.saveUndoStep() #"+this.undoStack.length+" created for "+undoableData.actionString);
        //console.log( "now "+this.undoStack.length+" undos, "+this.redoStack.length+" redos available.");
        Dyn.mediator.publish( "undoManager:updated", {}); // update undo/redo menu item state
    }

    /**
     * Add to our last undo step.  Used during notification when the response to an undoable action is to create more undoable steps.
     * @param undoableData
     * @param undoHelper
     */
    addToLastUndoStep( undoableData, undoHelper ) {
        let Dyn = this.Dyn;
        Dyn.assert( this.hasUndoSteps(), "no last undo action to add to" );
        if( this.hasUndoSteps()) {
            var lastAction = this.undoStack[ this.undoStack.length - 1 ];
            lastAction.addExtraStep( new Dyn.UndoStep( undoableData, undoHelper ));
        }
    }

    /**
     * Save a new undo step, unless we instead update the last undo step because they are the
     * same type of step. Some small repeated actions in a row can then be treated
     * as a single undo step, rather than multiple.
     * E.g. User wants to select cases multiple times, undo or redo all at once.
     * @param undoableData
     * @param undoHelper
     * @param canUpdateCallBack( lastUndoableData, newUndoableData ) -- optional comparison function
     */
    saveOrUpdateLastUndoStep( undoableData, undoHelper, canUpdateCallBack = null ) {
        let Dyn = this.Dyn,
            canUpdateLastUndo = false;
        if( this.hasUndoSteps() ) {
            let lastAction = this.undoStack[this.undoStack.length - 1];
            canUpdateLastUndo = (undoHelper === lastAction.undoHelper
                && undoableData.actionID === lastAction.undoableData.actionID
                && (canUpdateCallBack ? canUpdateCallBack( lastAction.undoableData, undoableData ) : true )); // optional extra requirements that caller imposes
            if( canUpdateLastUndo ) {
                // update lastAction's restoreData data, so undo then redo takes us to the latest step in a sequence
                // NOTE:  lastAction's saveData stays the same, so undo takes us to the oldest step in a sequence
                lastAction.undoableData.restoreData = undoableData.restoreData;
                Dyn.debugUndo && console.log( "undoManager: updated last undo, "+this.undoStack.length+" undos, "+this.redoStack.length+" redos available.");
            }
        }
        if( !canUpdateLastUndo ) {
            // last undo is not the same, create a new undo step instead
            this.saveUndoStep( undoableData, undoHelper );
            //console.log( "undoManager: saved instead of updated undo, "+this.undoStack.length+" undos, "+this.redoStack.length+" redos available.");
        }
    }

    /**
     * perform the undo of the last action saved with saveUndoAction(),
     * along with any extra undo steps saved with addToLastUndoAction().
     */
    undo() {
        let Dyn = this.Dyn;
        if( this.hasUndoSteps()) {
            // undo this action
            var undoCount = this.undoStack.length,
                redoCount = this.redoStack.length,
                lastStep = this.undoStack.pop();
            Dyn.debugUndo && console.log( "undoManager.undo #"+undoCount+": "+lastStep.undoableData.actionString );
            lastStep.undo(); // Note: undo of action should not add to undo stack (again), so undo twice works properly
            this.redoStack.push( lastStep ); // instead we add to redo stack for redoing.
            Dyn.assert( this.undoStack.length === (undoCount-1), "Undo should reduce undo stack by 1, to "+(undoCount-1));
            Dyn.assert( this.redoStack.length === (redoCount+1), "Undo should only add 1 to redo stack, to "+(redoCount+1));
            Dyn.debugUndo && console.log( "undoManager.undo: now "+this.undoStack.length+" undos, "+this.redoStack.length+" redos available after undo of "+lastStep.undoableData.actionString );
            if ( typeof Dyn.onUserAction !== "undefined" ) {
                Dyn.onUserAction(
                    { type: lastStep.undoableData.actionID, value: "Undo "+lastStep.undoableData.actionString },
                    lastStep.getOrCreatePlotState( true, true ),
                    lastStep.getOrCreatePlotState( false ));
            }
            Dyn.mediator.publish( "undoManager:updated", {}); // update undo/redo menu item state
        } else {
            Dyn.debugUndo && console.log("Dyn.undoManager.undo() has nothing to undo");
        }
    }

    redo() {
        let Dyn = this.Dyn;
        if( this.hasRedoSteps()) {
            var undoCount = this.undoStack.length,
                redoCount = this.redoStack.length,
                nextStep = this.redoStack.pop();
            Dyn.debugUndo && console.log( "undoManager.redo #"+redoCount+": "+nextStep.undoableData.actionString );
            nextStep.redo(); // Note: we expect redo to add it's own undo, so we drop the nextStep reference here to dispose of it.
            Dyn.assert( this.undoStack.length === (undoCount+1), "Redo should add 1 to undo stack (to undo redoing), to "+(undoCount+1));
            Dyn.assert( this.redoStack.length === (redoCount-1), "Redo should only remove 1 from redo stack, to "+(redoCount-1));
            Dyn.debugUndo && console.log( "undoManager.redo: now "+this.undoStack.length+" undos, "+this.redoStack.length+" redos available after redo of "+nextStep.undoableData.actionString );
            Dyn.mediator.publish( "undoManager:updated", {}); // update undo/redo menu item state
        } else {
            Dyn.debugUndo && console.log("undoManager.redo() has nothing to redo");
        }
    }

    resetAll() {
        let Dyn = this.Dyn;
        this.clearUndoStack();
        this.clearRedoStack();
        Dyn.mediator.publish( "undoManager:updated", {}); // update undo/redo menu item state
        Dyn.debugUndo && console.log("undoManager.resetAll()");
    }

    clearUndoStack() {
        this.undoStack = []; // forget all data and references to undoable objects
    }

    clearRedoStack() {
        this.redoStack = []; // forget all data and references to redoable objects
    }

    hasUndoSteps() {
        return( this.undoStack.length > 0 );
    }

    hasRedoSteps() {
        return( this.redoStack.length > 0 );
    }

    /** get the undo action string if there is one and it is a string */
    getUndoActionString() {
        let numSteps = this.undoStack.length,
            string, step;
        if( numSteps ) {
            step = this.undoStack[numSteps - 1];
            if ( step && step.undoableData ) {
                string = step.undoableData.actionString;
                if ( typeof string === "string" ) {
                    return string;
                }
            }
        }
        return "";
    }

    /** get the redo action string if there is one and it is a string */
    getRedoActionString() {
        var numSteps = this.redoStack.length,
            string, step;
        if( numSteps ) {
            step = this.redoStack[numSteps - 1];
            if ( step && step.undoableData ) {
                string = step.undoableData.actionString;
                if ( typeof string === "string" ) {
                    return string;
                }
            }
        }
        return "";
    }

}
